export type ProductPropertyValue =
  | null
  | string
  | number
  | boolean
  | readonly ProductPropertyValue[]
  | { readonly [key: string]: ProductPropertyValue };

export type ProductProperties = Readonly<Record<string, ProductPropertyValue>>;

const MAX_DEPTH = 8;
const MAX_KEYS = 200;
const MAX_ARRAY_LENGTH = 200;
const MAX_STRING_LENGTH = 32_768;
const MAX_PAYLOAD_BYTES = 256 * 1024;

const SECRET_KEY =
  /^(?:password|passcode|pin|authorization|proxy_authorization|cookie|set_cookie|token|access_token|refresh_token|id_token|api_key|private_key|client_secret|session_token|session_secret|payment_token|credential|credentials|secret)$/i;
const SECRET_KEY_PART =
  /(?:^|[_$-])(?:password|passcode|authorization|cookie|credentials?|secret|access[_$-]?token|refresh[_$-]?token|api[_$-]?key|private[_$-]?key|client[_$-]?secret|session[_$-]?(?:token|secret))(?:$|[_$-])/i;
const JWT_VALUE = /^eyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{8,}$/;
const PREFIXED_CREDENTIAL_VALUE = /^(?:gh[pousr]_|sk_(?:live|test)_|pk_live_|xox[baprs]-)\S+$/i;
const SECRET_VALUE = /^(?:(?:Bearer|Basic)\s+\S+|AKIA[A-Z0-9]{16})$/i;
const PRIVATE_KEY_VALUE = /^-----BEGIN (?:RSA |EC |OPENSSH )?PRIVATE KEY-----/;
/**
 * Credential forms embedded inside longer strings (an `Error.message`, a
 * request log line, a URL in prose). `isSecretValue` only matches a whole
 * value, so ADR-081 Rule 8 needs these for the in-place redaction the
 * exception scrub performs: the surrounding message and stack survive, the
 * credential does not. Deliberately explicit shapes (headers, key=value pairs,
 * JWTs, cloud/vendor prefixes) rather than "any long opaque token": chunk
 * hashes and request ids are long opaque strings too, and losing them would
 * make the very stacks this scrub exists to preserve useless.
 */
const EMBEDDED_AUTH_HEADER =
  /\b((?:proxy[-_])?authorization\s*[:=]\s*)(?:(bearer|basic|digest|token)\s+)?[^\s,;"']+/gi;
const EMBEDDED_BEARER_SCHEME = /\b(bearer|basic)\s+[A-Za-z0-9._~+/=-]{8,}/gi;
const EMBEDDED_SECRET_PAIR =
  /\b((?:password|passcode|pin|access[_-]?token|refresh[_-]?token|id[_-]?token|token|api[_-]?key|apikey|client[_-]?secret|session[_-]?(?:token|secret))\s*[=:]\s*["']?)[^\s"'&#,;)]+/gi;
const EMBEDDED_JWT = /\beyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{8,}\b/g;
const EMBEDDED_PREFIXED_CREDENTIAL =
  /\b(?:gh[pousr]_|sk_(?:live|test)_|pk_live_|xox[baprs]-)[A-Za-z0-9_-]{6,}\b/gi;
const EMBEDDED_AWS_ACCESS_KEY = /\bAKIA[A-Z0-9]{16}\b/g;

/** Redacts credential forms embedded in a longer string, keeping the rest of the text (ADR-081 Rule 8). */
function redactEmbeddedCredentials(value: string): string {
  return value
    .replace(SECRET_URL_PARAMETER, '$1[REDACTED]')
    .replace(EMBEDDED_AUTH_HEADER, (_match, prefix: string, scheme?: string) =>
      scheme ? `${prefix}${scheme} [REDACTED]` : `${prefix}[REDACTED]`,
    )
    .replace(EMBEDDED_BEARER_SCHEME, '$1 [REDACTED]')
    .replace(EMBEDDED_SECRET_PAIR, '$1[REDACTED]')
    .replace(EMBEDDED_JWT, '[REDACTED]')
    .replace(EMBEDDED_PREFIXED_CREDENTIAL, '[REDACTED]')
    .replace(EMBEDDED_AWS_ACCESS_KEY, '[REDACTED]');
}

const SECRET_URL_PARAMETER =
  /([?&](?:password|passcode|pin|authorization|proxy_authorization|cookie|set_cookie|token|access_token|refresh_token|id_token|api_key|client_secret|session_token|session_secret|payment_token|code)=)[^&#]*/gi;

function isSecretValue(value: string): boolean {
  return (
    SECRET_VALUE.test(value) ||
    JWT_VALUE.test(value) ||
    PREFIXED_CREDENTIAL_VALUE.test(value) ||
    PRIVATE_KEY_VALUE.test(value)
  );
}

export class ProductPropertiesError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ProductPropertiesError';
  }
}

export interface SanitizedProductProperties {
  properties: Record<string, ProductPropertyValue>;
  removedSecretCount: number;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    value !== null && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype
  );
}

function sanitizeObject(
  value: Record<string, unknown>,
  depth: number,
  state: { removedSecretCount: number; seen: WeakSet<object> },
): Record<string, ProductPropertyValue> {
  if (depth > MAX_DEPTH) throw new ProductPropertiesError('Product properties exceed max depth');
  if (state.seen.has(value)) throw new ProductPropertiesError('Product properties must be acyclic');
  state.seen.add(value);
  try {
    const entries = Object.entries(value);
    if (entries.length > MAX_KEYS) {
      throw new ProductPropertiesError('Product property object has too many keys');
    }
    const output: Record<string, ProductPropertyValue> = {};
    for (const [key, entry] of entries) {
      if (key.length === 0 || key.length > 200) {
        throw new ProductPropertiesError('Product property key has invalid length');
      }
      if (SECRET_KEY.test(key) || SECRET_KEY_PART.test(key)) {
        state.removedSecretCount += 1;
        continue;
      }
      if (typeof entry === 'string' && isSecretValue(entry)) {
        state.removedSecretCount += 1;
        continue;
      }
      output[key] = sanitizeValue(entry, depth + 1, state);
    }
    return output;
  } finally {
    state.seen.delete(value);
  }
}

function sanitizeValue(
  value: unknown,
  depth: number,
  state: { removedSecretCount: number; seen: WeakSet<object> },
): ProductPropertyValue {
  if (depth > MAX_DEPTH) throw new ProductPropertiesError('Product properties exceed max depth');
  if (value === null || typeof value === 'boolean') return value;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new ProductPropertiesError('Numbers must be finite');
    return value;
  }
  if (typeof value === 'string') {
    if (value.length > MAX_STRING_LENGTH) {
      throw new ProductPropertiesError('Product property string is too long');
    }
    if (isSecretValue(value)) {
      state.removedSecretCount += 1;
      return '[REDACTED]';
    }
    const redacted = value.replace(SECRET_URL_PARAMETER, '$1[REDACTED]');
    if (redacted !== value) state.removedSecretCount += 1;
    return redacted;
  }
  if (typeof value !== 'object') {
    throw new ProductPropertiesError('Product properties must be JSON serializable');
  }
  if (Array.isArray(value)) {
    if (value.length > MAX_ARRAY_LENGTH) {
      throw new ProductPropertiesError('Product property array is too large');
    }
    if (state.seen.has(value))
      throw new ProductPropertiesError('Product properties must be acyclic');
    state.seen.add(value);
    try {
      return value.map((entry) => sanitizeValue(entry, depth + 1, state));
    } finally {
      state.seen.delete(value);
    }
  }
  if (!isPlainObject(value)) {
    throw new ProductPropertiesError('Product properties must contain plain JSON objects');
  }
  return sanitizeObject(value, depth, state);
}

/**
 * Credential scrub with none of the structural limits above (ADR-081 Rule 8:
 * secret-like keys and values MUST be removed, or the event rejected, before a
 * provider call).
 *
 * `sanitizeProductProperties` guards chart properties: it caps depth, key
 * count, array length and payload size and rejects anything that is not plain
 * JSON. Vendor-shaped payloads such as PostHog's `$exception` event (a
 * `$exception_list[].stacktrace.frames` array whose frames carry `undefined`
 * fields) trip those limits and would be replaced wholesale, losing the stack.
 * This scrub keeps the shape and applies Rule 8 only: a secret-like KEY is
 * deleted outright (recursively, arrays included) so neither the name nor the
 * value leaves the client; a secret-shaped VALUE under a non-secret key
 * (bearer strings, JWTs, cloud keys) becomes `[REDACTED]`; secret URL query
 * parameters are redacted in place. It never throws; a value it cannot walk (a
 * function, a class instance) is returned unchanged, and a cycle becomes
 * `'[CYCLE]'`.
 */
export function scrubCredentialProperties(input: unknown): unknown {
  return scrubValue(input, new WeakSet<object>());
}

/** True when a property name itself is secret-like and must be dropped (ADR-081 Rule 8). */
export function isSecretPropertyKey(key: string): boolean {
  return SECRET_KEY.test(key) || SECRET_KEY_PART.test(key);
}

function scrubValue(value: unknown, seen: WeakSet<object>): unknown {
  if (typeof value === 'string') {
    if (isSecretValue(value)) return '[REDACTED]';
    return redactEmbeddedCredentials(value);
  }
  if (value === null || typeof value !== 'object') return value;
  // A cycle cannot be JSON-serialised anyway; mark it rather than hand back the
  // unscrubbed original subtree.
  if (seen.has(value)) return '[CYCLE]';
  seen.add(value);
  try {
    if (Array.isArray(value)) return value.map((entry) => scrubValue(entry, seen));
    if (!isPlainObject(value)) return value;
    const output: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
      if (isSecretPropertyKey(key)) continue;
      output[key] = scrubValue(entry, seen);
    }
    return output;
  } finally {
    seen.delete(value);
  }
}

export function sanitizeProductProperties(input: unknown): SanitizedProductProperties {
  if (!isPlainObject(input)) {
    throw new ProductPropertiesError('Product event properties must be an object');
  }
  const state = { removedSecretCount: 0, seen: new WeakSet<object>() };
  const properties = sanitizeObject(input, 0, state);
  if (JSON.stringify(properties).length > MAX_PAYLOAD_BYTES) {
    throw new ProductPropertiesError('Product event properties exceed max payload size');
  }
  return { properties, removedSecretCount: state.removedSecretCount };
}

export function assertProductProperties(input: unknown): ProductProperties {
  return sanitizeProductProperties(input).properties;
}
